import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Card from "antd/es/card/Card";
import { addNewCrewMember } from "../../redux/actions/crewEmployee";
import { getAllZonesApi } from "../../apis/districts";
import { getAllTenantsApi } from "../../apis/tenants";
import CustomButton from "../../common/CustomButton";
import CustomInput from "../../common/CustomInput";
import CustomSelect from "../../common/CustomSelect";
import EmployeeLocations, { hasValidEmployeeLocations } from "./EmployeeLocations";

const roles = [
  { value: "DRIVER", label: "Driver" },
  { value: "EMT", label: "EMT" },
  { value: "HELPER", label: "Helper" },
  { value: "DOCTOR", label: "Doctor" }
];
const emptyForm = { employeeCode: "", employeeName: "", employeeFatherName: "", mobileNumber: "", employeeRole: "", zoneId: undefined, zoneName: undefined, tenantId: undefined, tenantName: undefined };
const emptyValidations = { employeeCode: false, employeeName: false, employeeFatherName: false, mobileNumber: false, employeeRole: false };

function AddCrewMember() {
  const [userDetails, setUserDetails] = useState({});
  const [crewMemberDetails, setCrewMemberDetails] = useState(emptyForm);
  const [locations, setLocations] = useState([{ latitude: null, longitude: null }]);
  const [zones, setZones] = useState([]);
  const [validations, setValidations] = useState(emptyValidations);
  const [resetKey, setResetKey] = useState(0);
  const [tenants, setTenants] = useState([]);
  const supervisors = useSelector((state) => state.supervisor?.data);
  const dispatch = useDispatch();

  const isOpsManager = userDetails?.role === "OPERATION_MANAGER";
  const isTenantSelected = !!crewMemberDetails?.tenantId;

  const resetForm = (user) => {
    const base = { ...emptyForm };
    if (user?.role === "OPERATION_MANAGER" && user?.zone) {
      base.zoneId = user.zone.id;
      base.zoneName = user.zone.name;
    }
    if (user?.tenant?.id) {
      base.tenantId = user.tenant.id;
      base.tenantName = user.tenant.name;
    }
    setCrewMemberDetails(base);
    setLocations([{ latitude: null, longitude: null }]);
    setValidations(emptyValidations);
    setResetKey((k) => k + 1);
  };

  const handleAddLeader = () => {
    if (!hasValidEmployeeLocations(locations)) return;
    const payload = {
      ...crewMemberDetails,
      locations: locations.map(({ latitude, longitude }) => ({
        latitude: Number(latitude),
        longitude: Number(longitude),
      })),
    };
    dispatch(addNewCrewMember(payload, () => resetForm(userDetails)));
  };

  const getZones = async () => {
    const response = await getAllZonesApi();
    if (response) setZones(response);
  };

  useEffect(() => {
    getZones();
    getAllTenantsApi().then((res) => { if (res) setTenants(res); });
    const parsed = JSON.parse(localStorage.getItem("currentUser"));
    setUserDetails(parsed);
    resetForm(parsed);
  }, []);
  const zoneValid = isOpsManager || isTenantSelected || !!crewMemberDetails?.zoneId;
  const allValid =
    Object.values(validations).every((v) => v) &&
    zoneValid &&
    hasValidEmployeeLocations(locations);
  // const clusterLeaders = supervisors
  // ?.filter((sup) => sup.role === "CLUSTER_LEADER")
  // .map((sup) => ({
  //   label: `${sup.name} (${sup.username})`,
  //   value: sup.supervisorId,
  // }));

  const operationManagers = supervisors
    ?.filter((sup) => sup.role === "OPERATION_MANAGER")
    .map((sup) => ({
      label: `${sup.name} (${sup.username})`,
      value: sup.supervisorId,
    }));
  return (
    <Card
      title="Add New Employee"
      bordered
      style={{ border: "2px solid rgb(0, 168, 204)" }}
      styles={{
        header: {
          borderBottom: "2px solid rgb(0, 168, 204)",
          fontSize: "2rem",
          textAlign: "center",
          color: "rgb(0, 168, 204)",
        },
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-evenly", flexWrap: "wrap" }}>
        <CustomInput
          key={`employeeCode-${resetKey}`}
          label="Employee ID"
          placeholder="Employee ID"
          value={crewMemberDetails?.employeeCode}
          onChange={(e) =>
            setCrewMemberDetails({ ...crewMemberDetails, employeeCode: e.target.value })
          }
          required
          requiredMessage="Employee ID is required"
          onValidationChange={(valid) => setValidations((v) => ({ ...v, employeeCode: valid }))}
        />
        {/* <CustomInput
          label="Employee Temp ID"
          placeholder="Employee Temp ID"
          value={crewMemberDetails?.employeeTempCode}
          onChange={(e) =>
            setCrewMemberDetails({ ...crewMemberDetails, employeeTempCode: e.target.value })
          }
        /> */}
        <CustomInput
          key={`employeeName-${resetKey}`}
          label="Name"
          placeholder="Enter Name"
          value={crewMemberDetails?.employeeName}
          onChange={(e) =>
            setCrewMemberDetails({ ...crewMemberDetails, employeeName: e.target.value })
          }
          required
          validation={{
            pattern: /^[A-Za-z\s]+$/,
            message: "Only letters and spaces allowed",
          }}
          onValidationChange={(valid) => setValidations((v) => ({ ...v, employeeName: valid }))}
        />
        <CustomInput
          key={`employeeFatherName-${resetKey}`}
          label="Father Name"
          placeholder="Father Name"
          value={crewMemberDetails?.employeeFatherName}
          onChange={(e) =>
            setCrewMemberDetails({ ...crewMemberDetails, employeeFatherName: e.target.value })
          }
          required
          validation={{
            pattern: /^[A-Za-z\s]+$/,
            message: "Only letters and spaces allowed",
          }}
          onValidationChange={(valid) =>
            setValidations((v) => ({ ...v, employeeFatherName: valid }))
          }
        />
        <CustomInput
          key={`mobileNumber-${resetKey}`}
          label="Mobile Number"
          placeholder="Mobile Number"
          value={crewMemberDetails?.mobileNumber}
          onChange={(e) =>
            setCrewMemberDetails({ ...crewMemberDetails, mobileNumber: e.target.value })
          }
          required
          validation={{
            pattern: /^[6-9]\d{9}$/,
            message: "Enter a valid 10-digit mobile number",
          }}
          onValidationChange={(valid) => setValidations((v) => ({ ...v, mobileNumber: valid }))}
        />
        <CustomSelect
          label="Role"
          placeholder="Role"
          value={crewMemberDetails?.employeeRole}
          options={roles}
          style={{ width: "8rem" }}
          onChange={(val) => {
            setCrewMemberDetails({ ...crewMemberDetails, employeeRole: val });
            setValidations((v) => ({ ...v, employeeRole: !!val }));
          }}
          required
        />
      </div>
      <div style={{ display: "flex", width: "100%", justifyContent: "center", alignItems: "center", gap: "1rem", marginTop: 20 }}>

        <CustomSelect
          label="Tenant"
          placeholder="Select Tenant"
          value={crewMemberDetails?.tenantId}
          options={tenants.map((t) => ({ label: t.name, value: t.id }))}
          style={{ width: "9rem" }}
          disabled={!!userDetails?.zone?.id || (isOpsManager && !!userDetails?.tenant?.id)}
          onChange={(val) => {
            const selected = tenants.find((t) => t.id === val);
            setCrewMemberDetails({
              ...crewMemberDetails,
              tenantId: selected?.id,
              tenantName: selected?.name,
              zoneId: undefined,
              zoneName: undefined,
            });
          }}
        />
        {(isOpsManager || userDetails?.role === "SUPER_ADMIN") && <CustomSelect
          label="Zone"
          placeholder="Select Zone"
          value={crewMemberDetails?.zoneId}
          options={zones.map((zone) => ({
            label: zone.name,
            value: zone.id
          }))}
          style={{ width: "7rem" }}
          required={!isOpsManager && !isTenantSelected}
          disabled={isOpsManager || isTenantSelected}
          onChange={(val) => {
            const selected = zones.find(z => z.id == val);
            setCrewMemberDetails({ ...crewMemberDetails, zoneId: selected?.id, zoneName: selected?.name });
          }}
        />}
        <div style={{ width: "100%", marginBottom: 16 }}>
          <label style={{ fontWeight: 500, display: "block", marginBottom: 8 }}>
            Employee Locations <span style={{ color: "red" }}>*</span>
          </label>
          <EmployeeLocations value={locations} onChange={setLocations} />
        </div>
        {/* <CustomSelect
          label="Cluster Leader"
          placeholder="Select Cluster Leader"
          value={crewMemberDetails?.clusterLeaderId}
          options={clusterLeaders}
          onChange={(val) => {
            setCrewMemberDetails({ ...crewMemberDetails, clusterLeaderId: val });
          }}
          required
        />
        <CustomSelect
          label="Operation Manager"
          placeholder="Select Operation Manager"
          value={crewMemberDetails?.operationManagerId}
          options={operationManagers}
          onChange={(val) => {
            setCrewMemberDetails({ ...crewMemberDetails, operationManagerId: val });
          }}
          required
        /> */}
        <CustomButton
          type="primary"
          onClick={handleAddLeader}
          title="Add Employee"
          disabled={!allValid}
        >
          Add Leader
        </CustomButton>
      </div>

    </Card>
  );
}

export default AddCrewMember;
